// Drift: min profil, adgangskode, sprog og notifikationer (klokken + push) på Supabase (del af SupabaseApi – se supabase.ts).
// Notifikationerne oprettes af databasen (trigger pixie_booking_notif m.fl.) og sendes som push af edge-funktionen send-push.
// Brugeren kan kun læse sine egne, markere dem læst og styre sine egne indstillinger og enheder (RLS på profilens id).
import { ApiFejl, type Api, type Bruger } from './api';
import type { Notifikation } from '../../core/typer';
import { NOTIF_TYPER, typerFor, type NotifType } from '../../core/notifikationer';
import { adgangskodeFejl, email as tjekEmail, rens, telefon as tjekTelefon } from '../../core/validering';
import type { SbKatalog, SbKontekst } from './sbKatalog';

export class SbProfil {
  constructor(private readonly c: SbKontekst, private readonly kat: SbKatalog, private readonly mig: () => Bruger | null) {}
  private medarbejder(): Bruger {
    const b = this.mig();
    if (!b || b.rolle === 'kunde') throw new ApiFejl('Log ind for at fortsætte.');
    return b;
  }

  // ---------- Min profil ----------
  minProfil: Api['minProfil'] = async () => {
    const b = this.medarbejder();
    const p = (await this.c.k(this.c.sb.from('profiler').select('navn, email, telefon, rolle').eq('id', b.id).single())) as { navn: string; email: string; telefon: string; rolle: Bruger['rolle'] };
    return { ...p, telefon: p.telefon ?? '', harKode: true };
  };
  gemMinProfil: Api['gemMinProfil'] = async (v) => {
    const b = this.medarbejder();
    const f: Record<string, string> = {};
    if (rens(v.navn, 100).length < 2) f.navn = 'Skriv dit navn.';
    if (!tjekTelefon(v.telefon)) f.telefon = 'Skriv et mobilnummer med 8 cifre.';
    const em = v.email != null ? tjekEmail(v.email) : null;
    if (v.email != null && !em) f.email = 'Skriv en gyldig e-mail.';
    if (Object.keys(f).length) throw new ApiFejl('Tjek de markerede felter.', f);
    await this.c.k(this.c.sb.rpc('gem_min_profil', { k: await this.c.kraevKlient(), p_navn: v.navn, p_telefon: v.telefon }));
    // Ny e-mail: Supabase sender et bekræftelseslink til den nye adresse; den skiftes først, når linket er brugt
    if (em && em !== (b.email ?? '').toLowerCase()) await this.c.k(this.c.sb.auth.updateUser({ email: em }));
    this.c.skift();
  };
  skiftKode: Api['skiftKode'] = async (gammel, ny) => {
    const b = this.medarbejder();
    const fejl = adgangskodeFejl(ny, b.email ?? ''); if (fejl) throw new ApiFejl(fejl, { ny: fejl });
    // Den nuværende adgangskode tjekkes ved at logge ind igen
    const { error } = await this.c.sb.auth.signInWithPassword({ email: b.email ?? '', password: gammel });
    if (error) throw new ApiFejl('Den nuværende adgangskode er forkert.', { gammel: 'Forkert adgangskode.' });
    await this.c.k(this.c.sb.auth.updateUser({ password: ny }));
  };
  saetSprog: Api['saetSprog'] = async (sprog) => {
    const b = this.medarbejder();
    if (!['da', 'en', 'pl', 'uk', 'ar', 'fa'].includes(sprog)) throw new ApiFejl('Ukendt sprog.');
    await this.c.k(this.c.sb.from('profiler').update({ sprog }).eq('id', b.id));
    b.sprog = sprog;
    this.c.skift();
  };

  // ---------- Notifikationer ----------
  notifikationer: Api['notifikationer'] = async () => {
    const b = this.mig();
    if (!b || b.rolle === 'kunde') return [];
    return (await this.c.k(this.c.sb.from('notifikationer').select('id, profil_id, type, titel, tekst, link, booking_id, tid, laest')
      .eq('profil_id', b.id).order('tid', { ascending: false }).limit(100))) as Notifikation[];
  };
  notifLaest: Api['notifLaest'] = async (ids) => {
    const b = this.medarbejder();
    if (ids !== 'alle' && !ids.length) return;
    let q = this.c.sb.from('notifikationer').update({ laest: new Date().toISOString() }).eq('profil_id', b.id).is('laest', null);
    if (ids !== 'alle') q = q.in('id', ids);
    await this.c.k(q);
    this.c.skift();
  };
  notifIndstillinger: Api['notifIndstillinger'] = async () => {
    const b = this.medarbejder();
    const [m, ind, enheder] = await Promise.all([
      this.kat.rolleEvner(),
      this.c.k(this.c.sb.from('notif_indstillinger').select('fra, lyd').eq('profil_id', b.id).maybeSingle()),
      this.c.k(this.c.sb.from('push_abonnementer').select('enhed, oprettet, endpoint').eq('profil_id', b.id).order('oprettet')),
    ]);
    return {
      typer: typerFor({ rolle: b.rolle as Exclude<Bruger['rolle'], 'kunde'> }, m).map(({ id, navn, beskrivelse }) => ({ id, navn, beskrivelse })),
      fra: ((ind as { fra: NotifType[] } | null)?.fra ?? []),
      lyd: (ind as { lyd?: boolean } | null)?.lyd !== false,
      enheder: (enheder as { enhed: string; oprettet: string; endpoint: string }[]),
    };
  };
  gemNotifIndstillinger: Api['gemNotifIndstillinger'] = async (fra) => {
    const b = this.medarbejder();
    const gyldige = new Set(NOTIF_TYPER.map((t) => t.id));
    await this.c.k(this.c.sb.from('notif_indstillinger').upsert({ profil_id: b.id, fra: [...new Set(fra)].filter((t) => gyldige.has(t)) }));
  };
  gemNotifLyd: Api['gemNotifLyd'] = async (lyd) => {
    const b = this.medarbejder();
    await this.c.k(this.c.sb.from('notif_indstillinger').upsert({ profil_id: b.id, lyd: !!lyd }, { onConflict: 'profil_id' }));
  };
  gemPushAbonnement: Api['gemPushAbonnement'] = async (a) => {
    const b = this.medarbejder();
    if (!/^https:\/\/[^\s]{10,900}$/.test(a.endpoint ?? '')) throw new ApiFejl('Telefonen gav en ugyldig adresse til notifikationer.');
    if (!/^[A-Za-z0-9_-]{40,200}$/.test(a.p256dh ?? '') || !/^[A-Za-z0-9_-]{10,60}$/.test(a.auth ?? '')) throw new ApiFejl('Telefonen gav ugyldige nøgler til notifikationer.');
    await this.c.k(this.c.sb.from('push_abonnementer').upsert({ endpoint: a.endpoint, profil_id: b.id, klient_id: await this.c.kraevKlient(),
      p256dh: a.p256dh, auth: a.auth, enhed: rens(a.enhed, 80) || 'Ukendt enhed', fejl: 0 }));
  };
  fjernPushAbonnement: Api['fjernPushAbonnement'] = async (endpoint) => {
    const b = this.medarbejder();
    await this.c.k(this.c.sb.from('push_abonnementer').delete().eq('endpoint', endpoint).eq('profil_id', b.id));
  };
  testNotifikation: Api['testNotifikation'] = async () => {
    this.medarbejder();
    await this.c.k(this.c.sb.rpc('test_notifikation', { k: await this.c.kraevKlient() }));
    this.c.skift();
  };
}
